import { useEffect, useState } from "react";
import { store } from "../../store";
import { shouldPolyfillLocale } from "../util/polyfillLocale";
import { logger } from "../util/logger";
import { languageSelectorEffect } from "../../i18n.config";

/**
 * Loads the current locale from Redux state, defaulting to "en-US" if not set.
 */
const loadLocaleFromRedux = (): string => {
  let currentLocale = store.getState().loginLocalStorage.loginFields.locale;
  if (currentLocale === "" || !currentLocale) {
    currentLocale = "en-US";
  }
  return currentLocale;
};

/**
 * Hook that handles locale loading and i18n configuration:
 * - Loads locale polyfills asynchronously
 * - Updates i18n configuration on locale changes
 * - Provides graceful error handling for unsupported locales
 */
export const useLocaleManagement = (): UseLocaleManagementReturn => {
  const [locale, setLocale] = useState(loadLocaleFromRedux);
  const [isLocaleLoaded, setIsLocaleLoaded] = useState(false);

  // Load locale polyfills for unsupported locales
  useEffect(() => {
    let isCancelled = false;

    const loadLocaleData = async () => {
      // Reset loaded state when locale changes so we don't report stale data
      setIsLocaleLoaded(false);
      try {
        await shouldPolyfillLocale(locale);
        if (!isCancelled) {
          setIsLocaleLoaded(true);
        }
      } catch (error) {
        logger.error("Error loading locale data:", error);
        // Ensure the app can render even if locale polyfill loading fails
        if (!isCancelled) {
          setIsLocaleLoaded(true);
        }
      }
    };

    loadLocaleData();

    return () => {
      isCancelled = true;
    };
  }, [locale]);

  // Handle language selector changes - runs once on mount, event listener handles subsequent changes
  useEffect(() => {
    const cleanup = languageSelectorEffect(locale, setLocale);
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isLocaleLoaded, currentLocale: locale };
};

export type UseLocaleManagementReturn = {
  isLocaleLoaded: boolean;
  currentLocale: string;
};

// Export for use in other parts of the app that need to get the locale
export { loadLocaleFromRedux as loadLocaleFromLocalStorage };
